import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { Firestore } from '../FirebaseAuth/firebase';
import { format, parseISO } from 'date-fns';

const SiteTrafficChart = ({ siteId, accountId, showSummary = false }) => {
  const [trafficData, setTrafficData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('3months');
  const [dataType, setDataType] = useState('both');
  const [chartType, setChartType] = useState('line');

  // Fetch daily traffic data for charts
  useEffect(() => {
    if (!siteId || !accountId) return;

    const unsubscribe = onSnapshot(
      collection(Firestore, `accounts/${accountId}/sites/${siteId}/trafficDaily`),
      (snapshot) => {
        try {
          const dailyData = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            dailyData.push({
              id: doc.id,
              ...data,
              dateObj: parseISO(data.date)
            });
          });

          // Sort by date and filter by time range
          const sortedData = dailyData.sort((a, b) => a.dateObj - b.dateObj);
          
          // Filter by time range
          let cutoffDate = new Date();
          
          switch (timeRange) {
            case '1month':
              cutoffDate.setMonth(cutoffDate.getMonth() - 1);
              break;
            case '3months':
              cutoffDate.setMonth(cutoffDate.getMonth() - 3);
              break;
            case '6months':
              cutoffDate.setMonth(cutoffDate.getMonth() - 6);
              break;
            default:
              cutoffDate.setMonth(cutoffDate.getMonth() - 3);
          }

          const filteredData = sortedData.filter(item => item.dateObj >= cutoffDate);
          
          // Group by date and combine organic/paid data
          const groupedData = {};
          filteredData.forEach(item => {
            const dateKey = item.date;
            if (!groupedData[dateKey]) {
              groupedData[dateKey] = {
                date: dateKey,
                dateObj: item.dateObj,
                organicTraffic: 0,
                paidTraffic: 0,
                organicCost: 0,
                paidCost: 0,
                totalTraffic: 0,
                totalCost: 0
              };
            }
            
            if (item.type === 'organic') {
              groupedData[dateKey].organicTraffic = item.traffic || 0;
              groupedData[dateKey].organicCost = item.cost || 0;
            } else if (item.type === 'paid') {
              groupedData[dateKey].paidTraffic = item.traffic || 0;
              groupedData[dateKey].paidCost = item.cost || 0;
            }
            
            groupedData[dateKey].totalTraffic = 
              groupedData[dateKey].organicTraffic + groupedData[dateKey].paidTraffic;
            groupedData[dateKey].totalCost = 
              groupedData[dateKey].organicCost + groupedData[dateKey].paidCost;
          });

          const chartData = Object.values(groupedData).sort((a, b) => a.dateObj - b.dateObj);
          setTrafficData(chartData);
          setLoading(false);
        } catch (err) {
          console.error('Error processing traffic data:', err);
          setError('Failed to process traffic data');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error fetching traffic data:', err);
        setError('Failed to fetch traffic data');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [siteId, accountId, timeRange]);

  // Fetch summary data
  useEffect(() => {
    if (!showSummary || !siteId || !accountId) return;

    const fetchSummary = async () => {
      try {
        const summaryDoc = await getDoc(
          doc(Firestore, `accounts/${accountId}/sites/${siteId}/trafficSummary/monthly`)
        );
        
        if (summaryDoc.exists()) {
          setSummaryData(summaryDoc.data());
        }
      } catch (err) {
        console.error('Error fetching summary data:', err);
      }
    };

    fetchSummary();
  }, [showSummary, siteId, accountId]);

  const chartDataFiltered = useMemo(() => {
    if (!trafficData) return [];
    
    return trafficData.map(item => ({
      ...item,
      formattedDate: format(item.dateObj, 'MMM dd'),
      // Filter data based on dataType selection
      displayOrganicTraffic: dataType === 'paid' ? 0 : item.organicTraffic,
      displayPaidTraffic: dataType === 'organic' ? 0 : item.paidTraffic,
      displayOrganicCost: dataType === 'paid' ? 0 : item.organicCost,
      displayPaidCost: dataType === 'organic' ? 0 : item.paidCost,
    }));
  }, [trafficData, dataType]);

  const summaryStats = useMemo(() => {
    if (!summaryData) return null;

    const getRecentMonthsData = (data, months = 3) => {
      const now = new Date();
      const monthKeys = [];
      
      for (let i = 0; i < months; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        monthKeys.push(format(date, 'yyyy-MM'));
      }
      
      return monthKeys.reduce((acc, month) => {
        if (data[month]) {
          acc.totalTraffic += data[month].totalTraffic || 0;
          acc.totalCost += data[month].totalCost || 0;
          acc.keywordCount += data[month].keywordCount || 0;
        }
        return acc;
      }, { totalTraffic: 0, totalCost: 0, keywordCount: 0 });
    };

    const organicStats = getRecentMonthsData(summaryData.organic || {});
    const paidStats = getRecentMonthsData(summaryData.paid || {});

    return {
      organic: organicStats,
      paid: paidStats,
      total: {
        totalTraffic: organicStats.totalTraffic + paidStats.totalTraffic,
        totalCost: organicStats.totalCost + paidStats.totalCost,
        keywordCount: organicStats.keywordCount + paidStats.keywordCount
      }
    };
  }, [summaryData]);

  const formatTrafficValue = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  // const formatCurrency = (value) => {
  //   return new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: 'USD',
  //     minimumFractionDigits: 0,
  //     maximumFractionDigits: 0
  //   }).format(value);
  // };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!trafficData || trafficData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No traffic data available yet. Data will appear once DataForSEO processing is complete.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Site Traffic Analytics
          </Typography>
          
          {showSummary && summaryStats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {formatTrafficValue(summaryStats.total.totalTraffic)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Traffic (3 months)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {formatTrafficValue(summaryStats.organic.totalTraffic)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organic Traffic
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {formatTrafficValue(summaryStats.paid.totalTraffic)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Paid Traffic
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <MenuItem value="1month">Last Month</MenuItem>
                  <MenuItem value="3months">Last 3 Months</MenuItem>
                  <MenuItem value="6months">Last 6 Months</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <ToggleButtonGroup
                value={dataType}
                exclusive
                onChange={(e, newDataType) => newDataType && setDataType(newDataType)}
                size="small"
                fullWidth
              >
                <ToggleButton value="both">Both</ToggleButton>
                <ToggleButton value="organic">Organic</ToggleButton>
                <ToggleButton value="paid">Paid</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <ToggleButtonGroup
                value={chartType}
                exclusive
                onChange={(e, newChartType) => newChartType && setChartType(newChartType)}
                size="small"
                fullWidth
              >
                <ToggleButton value="line">Line</ToggleButton>
                <ToggleButton value="bar">Bar</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip label="Organic" color="success" size="small" />
                <Chip label="Paid" color="warning" size="small" />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <ResponsiveContainer width="100%" height={400}>
          {chartType === 'line' ? (
            <LineChart data={chartDataFiltered}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="formattedDate" 
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickFormatter={formatTrafficValue}
              />
              <Tooltip 
                labelFormatter={(label) => `Date: ${label}`}
                formatter={(value, name) => [
                  formatTrafficValue(value),
                  name === 'displayOrganicTraffic' ? 'Organic Traffic' :
                  name === 'displayPaidTraffic' ? 'Paid Traffic' : name
                ]}
              />
              <Legend />
              {(dataType === 'both' || dataType === 'organic') && (
                <Line 
                  type="monotone" 
                  dataKey="displayOrganicTraffic" 
                  stroke="#2e7d32" 
                  strokeWidth={2}
                  name="Organic Traffic"
                  dot={{ r: 4 }}
                />
              )}
              {(dataType === 'both' || dataType === 'paid') && (
                <Line 
                  type="monotone" 
                  dataKey="displayPaidTraffic" 
                  stroke="#ed6c02" 
                  strokeWidth={2}
                  name="Paid Traffic"
                  dot={{ r: 4 }}
                />
              )}
            </LineChart>
          ) : (
            <BarChart data={chartDataFiltered}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="formattedDate" 
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickFormatter={formatTrafficValue}
              />
              <Tooltip 
                labelFormatter={(label) => `Date: ${label}`}
                formatter={(value, name) => [
                  formatTrafficValue(value),
                  name === 'displayOrganicTraffic' ? 'Organic Traffic' :
                  name === 'displayPaidTraffic' ? 'Paid Traffic' : name
                ]}
              />
              <Legend />
              {(dataType === 'both' || dataType === 'organic') && (
                <Bar 
                  dataKey="displayOrganicTraffic" 
                  fill="#2e7d32" 
                  name="Organic Traffic"
                />
              )}
              {(dataType === 'both' || dataType === 'paid') && (
                <Bar 
                  dataKey="displayPaidTraffic" 
                  fill="#ed6c02" 
                  name="Paid Traffic"
                />
              )}
            </BarChart>
          )}
        </ResponsiveContainer>

        {summaryData && summaryData.lastUpdated && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Last updated: {summaryData.lastUpdated.toDate?.()?.toLocaleDateString() || 'Unknown'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default SiteTrafficChart;